import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import { defineMessages, useIntl, FormattedMessage } from "react-intl";
import { Spin, Table } from "antd";
import styled from "styled-components";
import { Redirect } from "react-router";
import queryString from "query-string";

import { history } from "../../../store";
import CloudChannelsServices from "../../../services/cloudChannels";

import { selectors as MQTT_CONNECTION_SELECTORS } from "../../../ducks/mqtt";
import { selectors as NODE_CHANNEL_SELECTORS } from "../../../ducks/nodeChannels";
import { selectors as ACCOUNT_SELECTORS } from "../../../ducks/account";
import { selectors as LOADING_SELECTORS } from "../../../ducks/loadingData";

import { QUICKSTREAM_METHOD_V2 } from "../../../lib/utils/constants";
import themeColor from "../../../lib/style/theme";

import AddNewChannelButton from "../../../components/AddNewChannelButton";
import NewNodeChannelModal from "../NewNodeChannelModal";

import NodeChannelsListColumns from "./NodeChannelsListColumns";

import { errorNotification } from "../../../lib/utils/notification";
import queryURL from "../../../lib/utils/queryURL";

const translations = defineMessages({
  wrongCloudNodeName: {
    id: "general.wrongCloudNodeName",
    defaultMessage: "Node does not exist in Quickstream Cloud",
  },
});

const NodeChannelsList = ({ channels, loggedToCloud, cnn, cwid, nodes, numberOfChannels }) => {
  const { formatMessage } = useIntl();
  const [visibleNewNodeChannelForm, setVisibleNewNodeChannelForm] = useState(false);
  const {
    location: { search },
  } = window;

  const parsedQueryString = queryString.parse(search);

  const nodeData = nodes && nodes.find((node) => node.cwid === cwid);

  const [dataFromCloudFlag, setDataFromCloudFlag] = useState(false);
  const isLoadingChannelList = useSelector(LOADING_SELECTORS.getLoadingNodeChannels);

  const channelsJs = channels && channels.toJS();

  useEffect(() => {
    const pagination = window.localStorage.getItem(`${cwid}-paginationNodeChannelList`);

    if (parsedQueryString.page === "last" && numberOfChannels > 0) {
      const parsedPagination = queryString.parse(pagination);
      const parsedPage = Math.ceil(numberOfChannels / (+parsedPagination?.pageSize || 10));
      history.replace(`/node/${cwid}/${cnn}/channels?page=${parsedPage}&pageSize=${+parsedPagination?.pageSize || 10}`);

      return;
    }

    if (!search && pagination) {
      history.replace(`/node/${cwid}/${cnn}/channels${pagination}`);
    }
  }, [search, numberOfChannels, parsedQueryString, cwid, cnn]);

  useEffect(() => {
    if (channelsJs.length < 0 || dataFromCloudFlag) {
      return;
    }

    async function fetchPermissionDescription({ cloudIds, channelId }) {
      if (loggedToCloud) {
        await CloudChannelsServices.getChannelPermissionsDescription(
          { cloudIds, channelId, cwid },
          {
            errorNotification: errorNotification(formatMessage),
          }
        );
      }
    }

    const qsOutputs = {};
    Object.values(channelsJs).forEach((channel) => {
      const hasNonmuxedOutputs = channel.config?.nonMuxedOutputs && channel.config?.nonMuxedOutputs.length > 0;
      const hasMuxedOutputs = channel.config?.muxedOutputs && channel.config?.muxedOutputs.length > 0;

      if (hasNonmuxedOutputs) {
        // eslint-disable-next-line no-unused-expressions
        channel.config?.nonMuxedOutputs.forEach((nonMuxedOutput) => {
          if (nonMuxedOutput.type === QUICKSTREAM_METHOD_V2.outQSDirect.value) {
            if (qsOutputs[channel.channelId]) {
              qsOutputs[channel.channelId] = [...qsOutputs[channel.channelId], nonMuxedOutput.cloudId];
            } else {
              qsOutputs[channel.channelId] = [nonMuxedOutput.cloudId];
            }
          }
        });
      }

      if (hasMuxedOutputs) {
        // eslint-disable-next-line no-unused-expressions
        channel.config?.muxedOutputs.forEach((muxedOutput) => {
          // eslint-disable-next-line no-unused-expressions
          muxedOutput?.outputs?.forEach((muxedOutputOutput) => {
            if (muxedOutputOutput.type === QUICKSTREAM_METHOD_V2.outQSDirect.value) {
              if (qsOutputs[channel.channelId]) {
                qsOutputs[channel.channelId] = [...qsOutputs[channel.channelId], muxedOutputOutput.cloudId];
              } else {
                qsOutputs[channel.channelId] = [muxedOutputOutput.cloudId];
              }
            }
          });
        });
      }
    });

    Object.entries(qsOutputs).forEach((output) => {
      const [channelId, cloudIds] = output;

      const stringCloudIds = JSON.stringify(cloudIds);

      fetchPermissionDescription({ channelId, cloudIds: stringCloudIds, cwid });
      setDataFromCloudFlag(true);
    });
  }, [channels, channelsJs, cwid, dataFromCloudFlag, formatMessage, loggedToCloud]);

  const data = channelsJs && Object.entries(channelsJs).map(([, item]) => item);

  if (nodes && nodes.length > 0 && !nodeData) {
    errorNotification(formatMessage)(translations.wrongCloudNodeName);

    return <Redirect to="/qs-hub/hub-channels" />;
  }

  const handleChange = (paginationTable) => {
    queryURL({ parameter: "page", value: paginationTable.current });
    queryURL({ parameter: "pageSize", value: paginationTable.pageSize });
    window.localStorage.setItem(
      `${cwid}-paginationNodeChannelList`,
      `?page=${paginationTable.current}&pageSize=${paginationTable.pageSize}&tab=${parsedQueryString.tab}`
    );
  };

  return (
    <>
      {isLoadingChannelList && (
        <StyledSpin
          size="small"
          tip={<FormattedMessage id="ChannelList.loadingChannel" defaultMessage="Loading channel list..." />}
        />
      )}
      {!isLoadingChannelList && (
        <StyledTable
          dataSource={data}
          columns={NodeChannelsListColumns({ cwid, cnn })}
          rowClassName={(record) => {
            const isConfigWrongVersionRunning = record?.status?.configWrongVersionRunning;

            return isConfigWrongVersionRunning ? "wrongVersionRunning" : null;
          }}
          rowKey={(record) => record.channelId}
          scroll={{ x: 1080 }}
          onChange={handleChange}
          pagination={{
            current: +parsedQueryString?.page || 1,
            pageSize: +parsedQueryString?.pageSize || 10,
            showSizeChanger: true,
          }}
        />
      )}
      {nodeData && <AddNewChannelButton setVisibleNewNodeChannelForm={setVisibleNewNodeChannelForm} />}
      {nodeData && (
        <NewNodeChannelModal
          cwid={cwid}
          setVisibleNewNodeChannelForm={setVisibleNewNodeChannelForm}
          visibleNewNodeChannelForm={visibleNewNodeChannelForm}
        />
      )}
    </>
  );
};

const StyledSpin = styled(Spin)`
  width: 100%;
  margin: 100px 0;
`;

const StyledTable = styled(Table)`
  .wrongVersionRunning {
    background-color: ${themeColor.lightRed};
  }
`;

NodeChannelsList.propTypes = {
  capabilities: PropTypes.shape({
    audioCodecs: PropTypes.arrayOf(PropTypes.string).isRequired,
    audioFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
    audioStreamTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    inputTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    otherStreamTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    videoCodecs: PropTypes.arrayOf(PropTypes.string).isRequired,
    videoFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
    videoStreamTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  channels: ImmutablePropTypes.map.isRequired,
  loggedToCloud: PropTypes.string,
  cwid: PropTypes.string,
  cnn: PropTypes.string,
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      cnn: PropTypes.string.isRequired,
      cwid: PropTypes.string.isRequired,
    })
  ).isRequired,
  numberOfChannels: PropTypes.number.isRequired,
};

NodeChannelsList.defaultProps = {
  capabilities: null,
  cnn: null,
  cwid: null,
  loggedToCloud: null,
};

const mapStateToProps = (state) => ({
  channels: NODE_CHANNEL_SELECTORS.getChannels(state),
  numberOfChannels: NODE_CHANNEL_SELECTORS.numberOfChannels(state),
  loggedToCloud: ACCOUNT_SELECTORS.getUser(state),
  nodes: MQTT_CONNECTION_SELECTORS.getNodes(state),
});

export default connect(mapStateToProps, null)(NodeChannelsList);
